import React, { useCallback, useEffect, useState } from "react";
import { Edit, ExternalLink, Eye, MoreHorizontal, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PaginationControls } from "@/components/common/PaginationControl";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { TierApi, TierItem, GetAllTiersRequest } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { useToast } from "@/hooks/use-toast";
import { getFormattedDateTime } from "@/utils";
import { CreateTierForm } from "./CreateTierForm";

export default function Tier() {
  const { toast } = useToast();
  const [tiers, setTiers] = useState<TierItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<TierItem | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const fetchTiers = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload: GetAllTiersRequest = {
        p_PageNumber: page + 1,
        p_PageSize: pageSize,
      };
      const response = await TierApi.getAllTiers(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Warning",
          description: response.error_msg,
          variant: "destructive",
        });
      }

      const list = Array.isArray(response.getAllTiersList)
        ? response.getAllTiersList
        : [];
      setTiers(list);
      setTotalRecords(Number(response.total_records) || list.length);
    } catch (error) {
      console.error("Error fetching tiers:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch tiers",
        variant: "destructive",
      });
      setTiers([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  const totalPages = Math.max(
    1,
    Math.ceil((totalRecords || 0) / (pageSize || 1))
  );

  const formatNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "-";
    }
    return Number(value).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const openDetails = (tier: TierItem) => {
    setSelectedTier(tier);
    setIsEditMode(false);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setIsEditMode(false);
    setSelectedTier(null);
  };

  const tableColumns = [
    {
      header: "Tier Name",
      accessorKey: "tierName" as keyof TierItem,
      cell: (row: TierItem) => (
        <span className="font-medium text-gray-900">{row.tierName || "-"}</span>
      ),
    },
    {
      header: "Tier Type",
      accessorKey: "tierType" as keyof TierItem,
      cell: (row: TierItem) => (
        <Badge variant="secondary" className="font-normal">
          {row.tierType || "-"}
        </Badge>
      ),
    },
    {
      header: "From Value",
      accessorKey: "fromValue" as keyof TierItem,
      cell: (row: TierItem) => formatNumber(row.fromValue),
    },
    {
      header: "To Value",
      accessorKey: "toValue" as keyof TierItem,
      cell: (row: TierItem) => formatNumber(row.toValue),
    },
    {
      header: "Percentage",
      accessorKey: "percentage" as keyof TierItem,
      cell: (row: TierItem) =>
        row.percentage === null || row.percentage === undefined
          ? "-"
          : `${formatNumber(row.percentage)}%`,
    },
    {
      header: "Created Date",
      accessorKey: "createdDate" as keyof TierItem,
      cell: (row: TierItem) => getFormattedDateTime(row.createdDate) || "-",
    },
    {
      header: "Actions",
      id: "actions",
      cell: (row: TierItem) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white min-w-[10rem] p-1"
          >
            <DropdownMenuItem onClick={() => openDetails(row)}>
              <Eye className="mr-2 h-4 w-4" />
              <span>View Details</span>
            </DropdownMenuItem>
            {row.explorerUrl ? (
              <DropdownMenuItem
                onClick={() =>
                  window.open(row.explorerUrl!, "_blank", "noopener,noreferrer")
                }
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                <span>View On Blockchain</span>
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const detailRows = selectedTier
    ? [
        { label: "Tier Name", value: selectedTier.tierName || "-" },
        { label: "Tier Type", value: selectedTier.tierType || "-" },
        { label: "From Value", value: formatNumber(selectedTier.fromValue) },
        { label: "To Value", value: formatNumber(selectedTier.toValue) },
        {
          label: "Percentage",
          value:
            selectedTier.percentage === null ||
            selectedTier.percentage === undefined
              ? "-"
              : `${formatNumber(selectedTier.percentage)}%`,
        },
        { label: "Client ID", value: selectedTier.clientId || "-" },
        {
          label: "Created Date",
          value: getFormattedDateTime(selectedTier.createdDate) || "-",
        },
        {
          label: "Updated Date",
          value: getFormattedDateTime(selectedTier.updatedDate) || "-",
        },
        { label: "Tx Hash", value: selectedTier.txHash || "-" },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Tier</h1>
        <p className="text-muted-foreground">
          Manage member tiers and loyalty levels
        </p>
      </div>

      {isLoading ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-800">All Tiers</h2>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center gap-2 bg-green-600 text-white hover:bg-green-700"
            >
              <Plus className="h-4 w-4" />
              Create Tier
            </Button>
          </div>

          <DataTable
            title=""
            data={tiers}
            cellClassName="py-6 text-gray-700"
            columns={tableColumns}
          />

          {tiers.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm">
              No tiers found.
            </div>
          )}

          {totalRecords > 0 && (
            <div className="mt-4 flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Rows per page:</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(parseInt(value, 10));
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="30">30</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {totalPages > 1 && (
                <PaginationControls
                  currentPage={page}
                  totalPages={totalPages}
                  onPrevHandler={() => setPage((p) => Math.max(0, p - 1))}
                  onNextHandler={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  onPageChange={(newPage) => setPage(newPage)}
                />
              )}
            </div>
          )}
        </div>
      )}

      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Tier</DialogTitle>
          </DialogHeader>
          <CreateTierForm
            onSuccess={() => {
              setIsCreateModalOpen(false);
              if (page !== 0) {
                setPage(0);
              } else {
                fetchTiers();
              }
            }}
            onCancel={() => setIsCreateModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDetailsModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeDetailsModal();
          } else {
            setIsDetailsModalOpen(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? "Edit Tier" : "Tier Details"}
            </DialogTitle>
          </DialogHeader>

          {selectedTier && isEditMode ? (
            <CreateTierForm
              tier={selectedTier}
              onSuccess={() => {
                closeDetailsModal();
                fetchTiers();
              }}
              onCancel={() => setIsEditMode(false)}
            />
          ) : selectedTier ? (
            <>
              <div className="space-y-3 py-2">
                {detailRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-start justify-between gap-4 border-b border-gray-100 pb-2 last:border-0"
                  >
                    <span className="text-sm text-gray-500">{row.label}</span>
                    <span className="text-sm font-medium text-gray-900 text-right break-all">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                {selectedTier.explorerUrl ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      window.open(
                        selectedTier.explorerUrl!,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Blockchain
                  </Button>
                ) : null}
                <Button
                  type="button"
                  onClick={() => setIsEditMode(true)}
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
